import React, { useMemo, useCallback } from "react";
import { get } from "lodash";
import { BetRepeatInfo, VisualSelections } from "@tvg/ts-types/Bet";
import { BettingInterest, Probable, RaceWagerType } from "@tvg/ts-types/Race";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import { BetTypeCell, WebScrollbar } from "@tvg/design-system";
import formatCurrency from "@tvg/formatter/currency";
import { BetGroupStatus } from "@tvg/desktop-bet/src/types";
import { SpecialGroupBets } from "../../types";
import { BetSummarySpecialBetsContainer } from "./styled-components";
import {
  getMultiplierSpecialGroup,
  removeCoupledEntries
} from "../../utils/betUtils";
import {
  getCurrentProbableAmount,
  mapSelectionsForWPSProbables
} from "../../utils/getWPSProbableValue";

interface BetSummaryProps {
  numWagerableRunners: string[] | undefined;
  isSpecialGroup: boolean;
  allBetsPlaced: boolean;
  probables: Probable[];
  fullSelections: VisualSelections[];
  betSelectionsNumbers: string[][];
  betAmount: number;
  betTotal: string;
  specialGroupBets?: SpecialGroupBets;
  specialGroupBetsWithOutScratches: SpecialGroupBets;
  approxPayout?: string;
  typeFromRace?: RaceWagerType | null;
  betGroupStatus: BetGroupStatus;
  raceNumber?: string;
  scratches: string[][];
  onRepeatWager?: (betRepeatInfo?: BetRepeatInfo) => void;
  placeBetSuccess?: boolean;
  bettingInterests?: BettingInterest[];
}

export const BetSummary = ({
  numWagerableRunners,
  probables,
  betSelectionsNumbers,
  isSpecialGroup,
  fullSelections,
  betAmount,
  betTotal,
  specialGroupBets,
  specialGroupBetsWithOutScratches,
  approxPayout,
  typeFromRace,
  betGroupStatus,
  allBetsPlaced,
  raceNumber,
  scratches,
  onRepeatWager,
  placeBetSuccess,
  bettingInterests
}: BetSummaryProps) => {
  const isBetRepeatable = useMemo(
    () => placeBetSuccess && !scratches.some((scrat) => scrat.length > 0),
    [placeBetSuccess, scratches]
  );

  const handleRepeatWager = useCallback(
    (betInfo?: BetRepeatInfo) => {
      if (isBetRepeatable && typeof onRepeatWager === "function") {
        return () => onRepeatWager(betInfo);
      }
      return undefined;
    },
    [isBetRepeatable, onRepeatWager]
  );

  return !isSpecialGroup || !specialGroupBets ? (
    <BetTypeCell
      numWagerableRunners={numWagerableRunners}
      selections={fullSelections[0]}
      completeSelection={fullSelections}
      approximatedPayout={approxPayout ?? "0"}
      betAmount={formatCurrency(betAmount)}
      betTicket={formatCurrency(+betTotal)}
      qaLabel="confirm-bet-modal-summary"
      type={get(typeFromRace, "type.code", WagerTypeCodesEnum.WIN)}
      isKey={get(typeFromRace, "isKey", false)}
      isBox={get(typeFromRace, "isBox", false)}
      isWheel={get(typeFromRace, "isWheel", false)}
      betStatus="NONE"
      betTypeName={get(typeFromRace, "type.name", WagerTypeCodesEnum.WIN)}
      isLeg={get(typeFromRace, "legCount", 1) > 1}
      scratches={scratches}
      raceNumber={raceNumber}
      isRepeatable={isBetRepeatable}
      onRepeatBetHandler={handleRepeatWager()}
    />
  ) : (
    <WebScrollbar maxHeight="308px" padding={0} shadowPadding="0" useShadow>
      <BetSummarySpecialBetsContainer>
        {Object.keys(specialGroupBets).map((bet: string) => {
          // Will run each decoupled wager type from the WPS and find the wagerType from the race to get
          // it's name, to prevent to have hardcoded names not coming from service
          let approxPayoutWPS;
          let formattedBettingSelections = [];
          const hasSelections = specialGroupBets[bet].length > 0;
          const betType = get(typeFromRace, "types", []).find(
            (bt: RaceWagerType) => bt.type.code === bet
          );

          if (hasSelections) {
            // The selections will be formatter to be able to be processed by the probables method, removing any unecessary selections for each betType
            // eg: Win bet type will be formatter to only have win selections
            formattedBettingSelections = mapSelectionsForWPSProbables(
              betSelectionsNumbers,
              specialGroupBets[bet],
              betType?.type.code
            );
            // If the bet type has selections should calculate it's probable since on special bet types probables are calculated for each betType decoupled
            approxPayoutWPS = getCurrentProbableAmount(
              probables,
              betAmount,
              formattedBettingSelections,
              true,
              betType
            );
          }

          const processingState = betGroupStatus[bet];
          const betRepeatInfo: BetRepeatInfo | undefined = betType?.type && {
            wagerType: betType?.type,
            wagerAmount: betAmount,
            selections: {
              selection: specialGroupBets[bet].reduce(
                (acc, cur) => {
                  const selectedBI = (bettingInterests || []).find(
                    (bi) => +bi.biNumber === +cur.number
                  );
                  if (selectedBI) {
                    // @ts-ignore
                    acc[0].runners.push({ number: cur.number });
                  }

                  return acc;
                },
                [{ runners: [] }]
              )
            }
          };

          return (
            hasSelections && (
              <BetTypeCell
                numWagerableRunners={numWagerableRunners}
                key={`betSummary-${bet}`}
                qaLabel={`confirm-bet-modal-summary-${bet}`}
                selections={specialGroupBets[bet]}
                approximatedPayout={approxPayoutWPS ?? "0"}
                betAmount={formatCurrency(betAmount)}
                /** Each ticket value has a mix of 3 things bet amount, number of selections and it's multiplier, eg. Win/Place
                 * Has a multiplier of 2 bets and Win only 1 */
                betTicket={formatCurrency(
                  betAmount *
                    removeCoupledEntries(specialGroupBetsWithOutScratches[bet])
                      .length *
                    getMultiplierSpecialGroup(bet)
                )}
                type={bet as WagerTypeCodesEnum}
                betTypeName={get(betType, "type.name", WagerTypeCodesEnum.WIN)}
                betStatus={
                  processingState && !allBetsPlaced
                    ? processingState.status
                    : "NONE"
                }
                scratches={scratches}
                isLeg={false}
                isRepeatable={isBetRepeatable}
                onRepeatBetHandler={handleRepeatWager(betRepeatInfo)}
              />
            )
          );
        })}
      </BetSummarySpecialBetsContainer>
    </WebScrollbar>
  );
};
